import React, { useState, useEffect } from "react";
import { captainTcCardService } from "../services/captainTcCardService";
import { toast } from "react-toastify";

function CaptainTcCardAssignForm({ retailers, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    retail_business_id: "",
    prefix: "",
    notes: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [prefixes, setPrefixes] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);

  useEffect(() => {
    fetchPrefixes();
  }, []);

  const fetchPrefixes = async () => {
    try {
      const response = await captainTcCardService.getAvailablePrefixes();
      if (response.status) {
        setPrefixes(response.data);
      }
    } catch (error) {
      console.error("Failed to load prefixes", error);
    }
  };

  const fetchCards = async (prefix) => {
    if (!prefix) {
      setCards([]);
      setSelectedCardIds([]);
      return;
    }
    setLoadingCards(true);
    try {
      const response = await captainTcCardService.getUnassignedCards(prefix);
      if (response.status) {
        setCards(response.data);
        setSelectedCardIds([]);
      }
    } catch (error) {
      console.error("Failed to load cards", error);
      toast.error("Failed to load unassigned cards");
    } finally {
      setLoadingCards(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (name === "prefix") {
      fetchCards(value);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCardIds(cards.map(c => c.id));
    } else {
      setSelectedCardIds([]);
    }
  };

  const handleSelectCard = (id) => {
    setSelectedCardIds(prev => 
      prev.includes(id) ? prev.filter(cardId => cardId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.retail_business_id || selectedCardIds.length === 0) {
      toast.error("Please select a retail business and at least one card.");
      return;
    }
    setLoading(true);
    try {
      const response = await captainTcCardService.assignCards({
        retail_business_id: formData.retail_business_id,
        card_ids: selectedCardIds,
        notes: formData.notes,
      });
      if (response.status) {
        toast.success(response.message || "Cards assigned successfully.");
        onSuccess();
      } else {
        toast.error(response.message || "Failed to assign cards.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to assign cards");
    } finally {
      setLoading(false);
    }
  };

  const isAllSelected = cards.length > 0 && selectedCardIds.length === cards.length;

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={modalStyle}>
        <h3>Assign Captain TC Gold Cards</h3>
        <form onSubmit={handleSubmit} style={formStyle}>
          
          <div style={{ display: 'flex', gap: '20px' }}>
            <div className="form-group" style={{ ...groupStyle, flex: 1 }}>
              <label>Retail Business *</label>
              <select
                name="retail_business_id"
                value={formData.retail_business_id}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="">Select Retail Business</option>
                {retailers.map((r) => (
                  <option key={r.retail_business_id} value={r.retail_business_id}>
                    {r.retail_business_id} - {r.shop_details?.retail_name || `${r.first_name || ''} ${r.last_name || ''}`.trim()}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ ...groupStyle, flex: 1 }}>
              <label>Card Prefix / Batch</label>
              <select
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                style={inputStyle}
              >
                <option value="">Select Prefix</option>
                {prefixes.map((p) => (
                  <option key={p.prefix} value={p.prefix}>
                    {p.prefix} ({p.count} available)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.prefix && (
            <div className="form-group" style={groupStyle}>
              <label style={{ fontWeight: 'bold' }}>AVAILABLE BATCH</label>
              
              <div style={tableContainerStyle}>
                {loadingCards ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>Loading cards...</div>
                ) : (
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>CARD NUMBER</th>
                        <th style={thStyle}>UNIQUE SERIAL NUMBER</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>
                          Select All <br />
                          <input 
                            type="checkbox" 
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((card, index) => (
                        <tr key={card.id}>
                          <td style={tdStyle}>{index + 1}</td>
                          <td style={tdStyle}>{card.card_number}</td>
                          <td style={tdStyle}>{card.serial_number}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <input 
                              type="checkbox" 
                              checked={selectedCardIds.includes(card.id)}
                              onChange={() => handleSelectCard(card.id)}
                            />
                          </td>
                        </tr>
                      ))}
                      {cards.length === 0 && (
                        <tr>
                          <td colSpan="4" style={{ ...tdStyle, textAlign: 'center' }}>No unassigned cards found for this prefix.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
              <small>Selected Cards: {selectedCardIds.length}</small>
            </div>
          )}

          <div className="form-group" style={groupStyle}>
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              style={{ ...inputStyle, minHeight: "60px" }}
              maxLength={65535} 
            />
          </div>

          <div className="modal-actions" style={actionsStyle}>
            <button type="button" onClick={onClose} style={cancelBtnStyle} disabled={loading}>
              Cancel
            </button>
            <button type="submit" style={submitBtnStyle} disabled={loading || selectedCardIds.length === 0}>
              {loading ? "Assigning..." : "Assign Gold Reward Cards"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000
};
const modalStyle = {
  backgroundColor: "#fff", padding: "30px", borderRadius: "8px", width: "700px", maxWidth: "90%",
  maxHeight: "90vh", overflowY: "auto"
};
const formStyle = { display: "flex", flexDirection: "column", gap: "15px", marginTop: "20px" };
const groupStyle = { display: "flex", flexDirection: "column", gap: "5px" };
const inputStyle = { padding: "10px", border: "1px solid #ccc", borderRadius: "4px" };
const actionsStyle = { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" };
const btnBase = { padding: "10px 20px", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" };
const cancelBtnStyle = { ...btnBase, backgroundColor: "#f1f1f1", color: "#333" };
const submitBtnStyle = { ...btnBase, backgroundColor: "#1d4ed8", color: "#fff" };

const tableContainerStyle = {
  maxHeight: "300px",
  overflowY: "auto",
  border: "1px solid #ddd",
  borderRadius: "4px"
};
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
};
const thStyle = {
  backgroundColor: "#f9fafb",
  padding: "10px",
  borderBottom: "1px solid #ddd",
  textAlign: "left",
  fontSize: "13px",
  position: "sticky",
  top: 0,
  zIndex: 1
};
const tdStyle = {
  padding: "10px",
  borderBottom: "1px solid #eee",
  fontSize: "14px"
};

export default CaptainTcCardAssignForm;
